import { Header, Tab } from '@mairistem/composants';
import { GenericTextOperators, request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { TabPaneHeader } from '@/components/Autre';
import { ModalEncaissement } from '@/components/Encaissement/ModalEncaissement';
import ModalEncaisseDepassee from '@/components/Encaissement/ModalEncaissement/ModalEncaisseDepassee';
import { ModalRecu } from '@/components/Encaissement/ModalRecu';
import RecusListe from '@/components/Encaissement/RecusListe/RecusListe';
import FacturesListe from '@/components/Facture/FacturesListe';
import { FactureContext, RecuContext, RegieContext } from '@/contexts';
import { useEncaissement, useFacturesEncaissable } from '@/hooks';
import { getFactures } from '@/routes';
import type { ApiResponseType, FactureType, RecuEncaissementType } from '@/types';
import { useRegieStore } from '@/zustand';
import ComptantPanel from './ComptantPanel';

interface FacturesRecusPanelProps {
  onEncaissement?: () => void;
  onAnnulation?: () => void;
}

const FacturesRecusPanel = ({ onEncaissement, onAnnulation }: FacturesRecusPanelProps) => {
  const [modalEncaissement, setModalEncaissement] = React.useState<number | null>();
  const [modalRecu, setModalRecu] = React.useState<number | null>();

  /**
   * Toutes les factures du payeur qui est concerné par l'encaissement.
   * Si on filtre juste le tableau de facture qui vient du context, les factures qui ne sont pas
   * chargées dans la liste ne seront pas présentent dans l'encaissement
   */
  const [facturesEncaissement, setFacuresEncaissement] = React.useState<FactureType[]>([]);

  const facturesEncaissables = useFacturesEncaissable(facturesEncaissement);
  const { saveEncaissement, encaisseDepassee, resetEncaisseDepassee } = useEncaissement();

  const {
    total: totalRecus,
    recus,
    setRecus,
    setTotal: setTotalRecus,
  } = React.useContext(RecuContext);

  const {
    total: totalFactures,
    factures,
    setFactures,
    setTotal: setTotalFactures,
    setSearch: setSearchFactures,
    setSorts: setSortsFactures,
    setFilters: setFiltersFactures,
    applyDefaultFilters: applyDefaultFiltersFactures,
  } = React.useContext(FactureContext);

  const { regies, selectedRegie } = React.useContext(RegieContext);

  const autonome = useRegieStore((state) => state.regie?.autonome);

  const hasRecuParMail = React.useMemo(
    () => _.find(regies, { identifiant: selectedRegie })?.envoiRecuParMail || false,
    [selectedRegie, regies],
  );

  const loadFacturesEncaissement = React.useCallback((identifiantPayeur: number) => {
    request
      .get(
        // @ts-expect-error
        getFactures({
          filters: [
            {
              propertyName: 'identifiantPayeur',
              textOperator: GenericTextOperators.EGALE_A,
              value: identifiantPayeur,
            },
          ],
        }),
      )
      .then(({ data }: ApiResponseType<FactureType>) => {
        setFacuresEncaissement(data);
      });
  }, []);

  const handleEncaissement = React.useCallback(
    (id: number) => {
      setModalEncaissement(id);
      loadFacturesEncaissement(_.find(factures, { identifiant: id }).identifiantPayeur);
      toast.dismiss();
    },
    [factures, loadFacturesEncaissement],
  );

  const handleOpenRecu = React.useCallback((id: number) => {
    setModalRecu(id);
    toast.dismiss();
  }, []);

  // Fait apparaître un toast pour un reçu
  const toastRecu = React.useCallback(
    (recu: RecuEncaissementType) => {
      toast(
        <Header style={{ margin: 0 }}>
          Reçu n°
          {recu?.numero} créé
          <Header.Subheader style={{ fontStyle: 'italic', marginTop: 8 }}>
            Cliquer pour afficher le reçu
          </Header.Subheader>
        </Header>,
        {
          type: 'success',
          // containerId: 'toast-container-regie-encaissement',
          toastId: 'toast-recu',
          onClick: () => handleOpenRecu(recu.identifiant),
          style: { cursor: 'pointer' },
        },
      );
    },
    [handleOpenRecu],
  );

  /**
   * Un encaissement au comptant ne concerne aucune facture existante : il n'y a donc que
   * le reçu à ajouter à la liste. Le reste (réinitialisation de la saisie) est géré par le panel.
   */
  const handleRecuComptantCree = React.useCallback(
    (recu: RecuEncaissementType) => {
      setRecus((prev) => [recu, ...prev]);
      setTotalRecus((prev) => prev + 1);

      toastRecu(recu);
      onEncaissement?.();
    },
    [onEncaissement, setRecus, setTotalRecus, toastRecu],
  );

  /**
   * Gère l'encaissement d'une facture en créant un Reçu
   * On supprime la facture dans le cas où le reste est à 0
   * Sinon on la met à jour
   */
  const handleSaveEncaissement = React.useCallback(
    async (body: Record<string, unknown>, imprimerRecu = false, envoyerMail = false) => {
      const recu = await saveEncaissement(body, imprimerRecu, envoyerMail);

      if (recu) {
        setRecus((prev) => [recu, ...prev]);
        setTotalRecus((prev) => prev + 1);
        setModalEncaissement(null);
        // On remet la recherche à vide quand on encaisse
        setSearchFactures('');

        _.forEach(recu.factures, (f) => {
          if (_.sumBy(f.lignes, 'reste') === 0) {
            setTotalFactures((prev) => prev - 1);
            setFactures((prev) =>
              _.filter(prev, (facture) => facture.identifiant !== f.identifiant),
            );
          } else {
            setFactures((prev) =>
              _.map(prev, (facture) => {
                if (facture.identifiant === f.identifiant) {
                  return f;
                }

                return facture;
              }),
            );
          }
        });

        toastRecu(recu);
        onEncaissement?.();
      }
    },
    [
      onEncaissement,
      saveEncaissement,
      setFactures,
      setSearchFactures,
      setTotalFactures,
      setRecus,
      setTotalRecus,
      toastRecu,
    ],
  );

  // Au changement d'onglet, on RaZ les tris/filtres/recherche des FACTURES (comportement
  // d'origine). Les filtres des REÇUS sont au contraire conservés : le contexte (au-dessus
  // du Tab) les garde et la liste les réapplique à son montage (cf. getColumnFilterDefaults).
  const handleTabChange = React.useCallback(() => {
    setSortsFactures({ nomPayeur: 'asc', numero: 'asc' });
    setFiltersFactures(null);
    setSearchFactures('');
    applyDefaultFiltersFactures();
  }, [setSortsFactures, setFiltersFactures, setSearchFactures, applyDefaultFiltersFactures]);

  const panes = [
    autonome
      ? {
          menuItem: <TabPaneHeader id="comptant" name="ENCAISSEMENT AU COMPTANT" />,
          render: () => (
            <ComptantPanel envoiRecuParMail={hasRecuParMail} onRecuCree={handleRecuComptantCree} />
          ),
        }
      : {
          menuItem: (
            <TabPaneHeader
              id="factures"
              name={_.toUpper(`facture${totalFactures > 1 ? 's' : ''} à encaisser`)}
              count={totalFactures}
              loading={_.isNil(factures)}
            />
          ),
          render: () => <FacturesListe onEncaissement={handleEncaissement} />,
        },
    {
      menuItem: (
        <TabPaneHeader
          id="recus"
          name={_.toUpper(`reçu${totalRecus > 1 ? 's' : ''}`)}
          count={totalRecus}
          loading={_.isNil(recus)}
        />
      ),
      render: () => <RecusListe onOpen={handleOpenRecu} defaultFilteredOnRegisseur={false} />,
    },
  ];

  // Le title sert à contourner le title du panel
  return (
    <span title="">
      <Tab
        menu={{
          secondary: true,
          pointing: true,
          style: {
            height: 55,
            margin: 0,
          },
        }}
        panes={panes}
        onTabChange={handleTabChange}
      />
      {!_.isNil(modalEncaissement) && _.isNil(modalRecu) && (
        <ModalEncaissement
          envoiRecuParMail={hasRecuParMail}
          identifiantFacture={modalEncaissement}
          factures={facturesEncaissables}
          onClose={() => setModalEncaissement(null)}
          onSave={handleSaveEncaissement}
        />
      )}
      {!_.isNil(modalRecu) && (
        <ModalRecu
          envoiRecuParMail={hasRecuParMail}
          readonly={!_.isNil(modalEncaissement)}
          recu={_.find(recus, { identifiant: modalRecu })}
          onClose={() => {
            setModalRecu(null);
            setModalEncaissement(null);
          }}
          onAnnulation={onAnnulation}
        />
      )}
      {encaisseDepassee && (
        <ModalEncaisseDepassee
          encaisseGlobaleDepassee={encaisseDepassee.encaisseGlobaleDepassee}
          encaisseNumeraireDepassee={encaisseDepassee.encaisseNumeraireDepassee}
          onClose={resetEncaisseDepassee}
        />
      )}
    </span>
  );
};

export default FacturesRecusPanel;
